import { FeatureEventExperience } from '@mdlbeasts/nofomo.ui.web';
import { Box, Container, NoSsr, Stack, Typography } from '@mui/material';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { hasWindow } from 'utils/browser.utils';
import { isStructuredTextPresent } from 'utils/structured-text.utils';
import { aspectRatio } from 'utils/styles.utils';

import { Routes } from 'constants/routes.constants';
import { type EventLowestPrice, useTicketsEvent } from 'restful/tickets';
import { useEventDetailView, useOriginClick } from 'services/datalayer/browse';
import {
  EventCardFragment,
  PageEventFragment,
  VenueFragment,
} from 'types/api/dato-operations.types';

import { AffiliateNotice } from 'components/@affiliate/AffiliateNotice';
import { useAffiliateWatcher } from 'components/@affiliate/useAffiliateWatcher';
import DatoImage from 'components/@common/DatoImage';
import Description from 'components/@common/Description';
import Link from 'components/@common/Link';
import DynamicShareButton from 'components/@events/DynamicShareButton';
import EventCard from 'components/@events/EventCard';
import EventDetailPrice from 'components/@events/EventDetailPrice';
import EventHeader from 'components/@events/EventHeader';
import { isEventTicketingEnabled, isPastEvent } from 'components/@events/utils';
import VenueCard from 'components/@events/VenueCard';
import FavoriteToggle from 'components/@favorites/FavoriteToggle';
import LineupSelection from 'components/@lineup/LineupSelection';
import ScrollableList from 'components/@lists/ScrollableList';
import AppNavigation from 'components/@navigation/AppNavigation';
import PageBreadcrumbs from 'components/@navigation/PageBreadcrumbs';

import SettingLinksList from './SettingLinksList';

interface Props {
  event: PageEventFragment;
  moreEvents?: Array<EventCardFragment>;
  city: string;
  venue: VenueFragment;
  onSocialShared?: (platform: string) => void;
  eventLowestPrice?: EventLowestPrice | null;
  ticketingError: boolean;
}

const EventDetailContainer = ({
  event,
  moreEvents,
  onSocialShared,
  eventLowestPrice,
  ticketingError,
}: Props) => {
  useEventDetailView(event);

  const { data: ticketsEvent } = useTicketsEvent(
    {
      id: event.ticketsEventId ?? '',
      timeslotId: event.ticketsTimeslotId ?? '',
    },
    { enabled: isEventTicketingEnabled(event) },
  );

  /**
   * Affiliate watcher - syncs affiliate
   */
  useAffiliateWatcher({
    allowAffiliateCommissions: ticketsEvent?.allowAffiliateCommissions ?? false,
  });

  const handleClick = useOriginClick('Related', 'Event detail');
  const url = hasWindow() ? window.location.href : '';

  const isDisabled = event.ticketsIntegration === 'disabled';
  const isTicketExternal =
    event.ticketsIntegration === 'external' && !!event.ticketsExternalUrl;
  const pastEvent = isPastEvent({
    endDatetime: event.endDatetime,
    startDatetime: event.startDatetime,
    timezone: event.timezone,
  });

  const eventActions = (
    <NoSsr>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <DynamicShareButton
          url={url}
          ariaTitle={event.title}
          onShare={() => onSocialShared?.('url')}
        />
        <FavoriteToggle type="event" value={event.slug} />
      </Box>
    </NoSsr>
  );

  return (
    <>
      <AppNavigation hideBottomNavigation={true}>
        <PageBreadcrumbs
          items={[{ href: Routes.CollectionDetail, label: `${event.title}` }]}
        />
        <Container sx={{ px: { xs: 1, md: 2 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '6fr 1fr 5fr' },

              gridTemplateAreas: {
                xs: `'cover' 'description'`,
                md: `'description . cover'`,
              },

              alignItems: 'start',
              mb: { md: 15 },
              mt: { md: 3 },
            }}
          >
            <Stack
              sx={{
                gridArea: 'cover',
                position: { md: 'sticky' },
                top: 40,
              }}
            >
              <Box
                my={{ xs: '24px' }}
                sx={{ display: { xs: 'block', md: 'none' } }}
              >
                <AffiliateNotice eventName={event.title} />
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 6,
                  ...aspectRatio(1, 1),
                }}
              >
                <DatoImage
                  data={event.cover}
                  fill
                  priority
                  style={{ objectFit: 'cover' }}
                  sizes="512px"
                />
                <Box
                  sx={{
                    position: 'absolute',
                    right: 5,
                    top: 5,
                    display: { xs: 'block', md: 'none' },
                  }}
                >
                  {eventActions}
                </Box>
              </Box>

              <Box
                sx={{
                  display: { xs: 'none', md: 'block' },
                  position: 'sticky',
                  bottom: 0,
                  border: '1px solid',
                  borderColor: 'grey.800',

                  bgcolor:
                    pastEvent ||
                    isDisabled ||
                    isTicketExternal ||
                    ticketingError
                      ? 'background.default'
                      : 'grey.900',

                  zIndex: 1000,
                  borderRadius: 4,
                  mt: 5,
                }}
              >
                <EventDetailPrice
                  eventLowestPrice={eventLowestPrice}
                  event={event}
                  isDisabled={isDisabled}
                  isTicketExternal={isTicketExternal}
                  isPastEvent={pastEvent}
                />
              </Box>
            </Stack>

            <Stack
              sx={{
                mx: { xs: 1, md: 0 },
                mt: { xs: 2, md: 0 },
                gridArea: 'description',
                minWidth: 0,
              }}
            >
              <Stack
                sx={{
                  mb: 8,
                }}
              >
                <Box
                  mb={{ md: '24px' }}
                  sx={{ display: { xs: 'none', md: 'block' } }}
                >
                  <AffiliateNotice eventName={event.title} />
                </Box>
                <Box
                  sx={{
                    mb: isStructuredTextPresent(event.description) ? 3 : 0,
                  }}
                >
                  <EventHeader
                    event={event}
                    actions={
                      <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                        {eventActions}
                      </Box>
                    }
                  />
                </Box>

                {isStructuredTextPresent(event.description) && (
                  <Box
                    sx={{
                      mb: 3,
                    }}
                  >
                    <Description description={event.description} />
                  </Box>
                )}

                <Box>
                  <FeatureEventExperience.EventExperience
                    linkedPages={event.linkedPages}
                    DatoImage={DatoImage}
                    Link={Link}
                  />
                </Box>
              </Stack>

              <LineupSelection slug={event.slug} />

              <Stack>
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{
                    mb: 2,
                    color: 'text.secondary',
                  }}
                >
                  <FormattedMessage id="event.header.tab.venue" />
                </Typography>
                <VenueCard venue={event.venue} />
              </Stack>

              <Box
                sx={{
                  my: { xs: 8, md: 0 },
                  mt: { md: 8 },
                }}
              >
                <SettingLinksList />
              </Box>
            </Stack>
          </Box>
        </Container>

        {!!moreEvents?.length && (
          <Stack
            sx={{
              mb: { xs: 8, md: 15 },
            }}
          >
            <Container>
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  color: 'text.secondary',
                  mb: 2,
                }}
              >
                <FormattedMessage id="event.header.tab.otherevents" />
              </Typography>
            </Container>

            <ScrollableList
              minWidthItem={{ xs: 260, md: 272 }}
              imageRefHeight={272}
              gap={3}
            >
              {moreEvents?.map((moreEvent) => (
                <EventCard
                  borderless={true}
                  event={moreEvent}
                  key={moreEvent.id}
                  onClick={handleClick}
                />
              ))}
            </ScrollableList>
          </Stack>
        )}
      </AppNavigation>
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.default',
          zIndex: 1000,
          borderTop: '1px solid',
          borderColor: 'grey.800',
        }}
      >
        <EventDetailPrice
          eventLowestPrice={eventLowestPrice}
          event={event}
          isDisabled={isDisabled}
          isTicketExternal={isTicketExternal}
          isPastEvent={pastEvent}
        />
      </Box>
    </>
  );
};

export default EventDetailContainer;
